import { JACKPOT_POINTS, type CrapsBet, type CrapsState } from '../../engine/games/craps';
import { money, withCommas } from '../../format';
import { Die } from '../parts';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

const ROLL_MS = 900;
const LOST_MS = 900;
const BANNER_MS = 1100;
const END_HOLD_MS = 250;
const POINTS = [4, 5, 6, 8, 9, 10];
const BETS: { bet: CrapsBet; name: string; note: string }[] = [
  { bet: 'pass', name: 'Pass', note: '7/11 win · 2/3/12 lose' },
  { bet: 'dontpass', name: "Don't Pass", note: '2/3 win · 12 pushes' },
  { bet: 'field', name: 'Field', note: 'One roll · 2 & 12 pay 2:1' },
];
const ORDER: CrapsBet[] = ['pass', 'dontpass', 'field']; // the engine's cycle order

/** Dice faces flickering while they tumble, landing on the roll. */
function tumble(t: number, seed: number): [number, number] {
  const k = Math.floor(t / 90);
  return [((k * 5 + seed) % 6) + 1, ((k * 7 + seed * 3) % 6) + 1];
}

export function CrapsTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.craps?.state as CrapsState | undefined;
  const anim = snapshot.anim;
  const luck = secondChanceFrame(snapshot);
  const lostDice = luck?.match(/Dice: (\d) \+ (\d)/);
  const lost: [number, number] | null = lostDice ? [Number(lostDice[1]), Number(lostDice[2])] : null;
  const firstEnd = lost ? ROLL_MS + LOST_MS + BANNER_MS : 0;
  const elapsed = useAnimClock(snapshot, actions, firstEnd + ROLL_MS + END_HOLD_MS);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, lost ? ROLL_MS + LOST_MS : null, () => ({ what: 'A losing roll is rolled again.', saved: true }), setBurst);

  // What the dice show right now.
  let dice = state?.dice ?? null;
  let rolling = false;
  if (anim && state?.dice) {
    if (lost && elapsed < firstEnd) {
      rolling = elapsed < ROLL_MS;
      dice = rolling ? tumble(elapsed, 1) : lost;
    } else {
      const t = elapsed - firstEnd;
      rolling = t < ROLL_MS;
      dice = rolling ? tumble(t, 4) : state.dice;
    }
  }
  const sum = dice ? dice[0] + dice[1] : null;
  const pointOn = state?.point ?? null;
  const bet = state?.bet ?? 'pass';

  const pick = (b: CrapsBet) => {
    if (!state || anim || pointOn !== null) return;
    const steps = (ORDER.indexOf(b) - ORDER.indexOf(bet) + ORDER.length) % ORDER.length;
    for (let i = 0; i < steps; i++) actions.tableOption('betType');
  };

  const message = state && !anim ? state.message : '';
  const tone = /win|made!|\+\$/i.test(message) ? 'win' : /lose|-\$|out\./i.test(message) ? 'loss' : '';

  return (
    <>
      <Stage className="craps-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        {/* A status display, not buttons (user, 2026-09-27): the point, if one is set. */}
        <div className="point-strip" role="status" aria-label={pointOn ? `The point is ${pointOn}` : 'No point: come-out roll'}>
          <span className="point-label">Point</span>
          <span className="point-nums" aria-hidden="true">
            {POINTS.map((p) => <span key={p} className={pointOn === p ? 'hit' : ''}>{p}</span>)}
          </span>
          <span className={`puck${pointOn ? ' on' : ''}`} aria-hidden="true">{pointOn ? 'ON' : 'OFF'}</span>
        </div>
        <div className={`dice-pair${rolling ? ' rolling' : ''}`}>
          {dice ? (
            <>
              <Die face={dice[0]} />
              <Die face={dice[1]} />
            </>
          ) : (
            <>
              <Die hidden />
              <Die hidden />
            </>
          )}
          {sum !== null && !rolling && <span className="dice-sum">{sum}</span>}
        </div>
        <p className="craps-note">
          {pointOn ? `The point is ${pointOn}. ${bet === 'pass' ? 'Roll it again before a 7.' : 'A 7 before it wins for you.'}` : 'Come-out roll.'}
          {state && state.pointStreak > 0 ? ` Pass points in a row: ${state.pointStreak} of ${JACKPOT_POINTS}.` : ''}
        </p>
        <div className="craps-bets" role="radiogroup" aria-label="Your bet">
          {BETS.map((b) => (
            <button key={b.bet} role="radio" aria-checked={bet === b.bet} className={`craps-bet${bet === b.bet ? ' on' : ''}`} onClick={() => pick(b.bet)} disabled={!!anim || pointOn !== null}>
              <span className="spot-name">{b.name}</span>
              <span className="spot-pays">{b.note}</span>
            </button>
          ))}
        </div>
        {pointOn !== null && state && <p className="craps-note">Your {money(state.stake)} is riding on the point.</p>}
        <StatusLine snapshot={snapshot} result={message ? withCommas(message) : undefined} tone={tone as '' | 'win' | 'loss'} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['betType']} onAct={() => setBurst(null)} />
    </>
  );
}
